import {
  type PlanDetailVO,
  type PaymentPlan,
  type BillingAccount,
  type AddOnsVO,
  type PriceVO,
  ADD_ON_IDS,
  FE_ADD_ON_ID,
} from '@ctct/domain-layer';
import { NumberFormat } from '@ctct/infrastructure-layer';
import { observabilityAppService } from '@ctct/myaccount-utils-static';
import type { AddOnItemOption } from '@ctct/payflow-ui';
import type { AddOnsPageStore, SelectedAddOn } from '@ctct/state-client-layer';

import type { TEventSource } from '../../../helpers';
import type { TranslationGetter, TranslationPaths } from '../../../types';
import { addOnsEventBus } from '../AddOnsEventBus';
import type { AddonsUrlAdapter } from '../types';

export class AddOnsService {
  private PREVIEWS_AMOUNT = 25;

  private readonly SMS_URL =
    'https://knowledgebase.constantcontact.com/email-digital-marketing/articles/KnowledgeBase/45188-About-Constant-Contacts-SMS-tool';

  constructor(
    private pageStore: AddOnsPageStore,
    private billingAccountQuery: BillingAccount | undefined,
    private addOnsQuery: AddOnsVO | undefined,
    private paymentPlanQuery: PaymentPlan | undefined,
    private planDetailQuery: PlanDetailVO | undefined,
    private t: TranslationGetter,
    private urlAdapter: AddonsUrlAdapter,
    private isStripe: boolean,
    private isSmsBundle = false,
    private isValueFocusedMWebPlanSelectionEnabled = false,
    private isSingleBillChildAccount = false
  ) {}

  public api() {
    return {
      getSelectedAddons: () => this.pageStore.getState().selectedAddOns,
      removeAddOn: (id: string) => {
        if (this.isSingleBillChildAccount) {
          return;
        }

        this.removeAddOn(id, 'api');
      },
      setSmsTier: (option: string) => {
        if (this.isSingleBillChildAccount) {
          return;
        }

        this.addAddOn(ADD_ON_IDS.SMS, option, 'api');
      },
      setIncludePurchased: (includePurchased: boolean) => {
        this.pageStore.setState({ includePurchased });
      },
      isSMSAddonPurchased: () => {
        if (this.addOnsQuery === undefined) {
          return false;
        }

        return this.addOnsQuery.getAddOns().some((addOn) => addOn.getIsSmsAddOn() && addOn.getPurchased());
      },
      openSmsBundleTierModal: () => {
        this.pageStore.setState({ smsBundleTierModalOpen: true });
      },
      closeSmsBundleTierModal: () => {
        this.pageStore.setState({ smsBundleTierModalOpen: false });
      },
    };
  }

  public data() {
    return {
      getAddOnIds: (): string[] => {
        if (this.addOnsQuery === undefined) {
          return [];
        }

        const addOnIds = this.addOnsQuery
          .getAddOns()
          .map((addOn) => addOn.getId())
          // SMS first
          .sort((a) => (a === ADD_ON_IDS.SMS ? -1 : 1));

        if (this.planDetailQuery && !this.planDetailQuery.isAuOrNz()) {
          // add xero addon to the end for non-AU/NZ plans
          addOnIds.push(FE_ADD_ON_ID.XERO);
        }

        return addOnIds;
      },
      getSmsAddOnId: () => ADD_ON_IDS.SMS,
      getIbpAddOnId: () => ADD_ON_IDS.IBP,
      getXeroAddonId: () => FE_ADD_ON_ID.XERO,
      isSmsAddOn: (id: string) => id === ADD_ON_IDS.SMS,
      isIbpAddOn: (id: string) => id === ADD_ON_IDS.IBP,
      isXeroAddOn: (id: string) => id === FE_ADD_ON_ID.XERO,
      getTitle: (id: string) => {
        return (
          {
            [ADD_ON_IDS.SMS]: this.t('application-layer.add-ons.sms.title'),
            [ADD_ON_IDS.IBP]: this.t('application-layer.add-ons.ibp.title'),
            [FE_ADD_ON_ID.XERO]: this.t('application-layer.add-ons.xero.title'),
          }[id] ?? ''
        );
      },
      getDescription: (id: string) => {
        return {
          [ADD_ON_IDS.SMS]: this.t('application-layer.add-ons.sms.description'),
          [ADD_ON_IDS.IBP]: this.t('application-layer.add-ons.ibp.description'),
          [FE_ADD_ON_ID.XERO]: this.t('application-layer.add-ons.xero.description'),
        }[id];
      },
      getSmsEINInfoKey: (): TranslationPaths => {
        return 'application-layer.add-ons.sms.footer';
      },
      getSmsFooterContent: () => {
        return {
          viewAdditionalSmsOptions: this.t('application-layer.add-ons.sms.view-additional-sms'),
          dialog: {
            title: this.t('application-layer.add-ons.sms-dialog.title'),
            description: this.t('application-layer.add-ons.sms-dialog.description'),
            button: this.t('application-layer.add-ons.sms-dialog.add-sms'),
            tableHeaders: [
              this.t('application-layer.add-ons.sms-dialog.table-header-1'),
              this.t('application-layer.add-ons.sms-dialog.table-header-2'),
              this.t('application-layer.add-ons.sms-dialog.table-header-3'),
            ] as [string, string, string],
          },
        };
      },
      getSmsKnowledgeBaseUrl: () => this.SMS_URL,
      getOptions: (addonId: string, selectedAddOns: SelectedAddOn[], prepay: string) => {
        // Get the account currency and discount percentage
        const currencySymbol = this.billingAccountQuery?.getCurrencySymbol();
        const discountPercentage = this.paymentPlanQuery?.getDiscountPercentage(prepay);

        if (!currencySymbol || discountPercentage === undefined) return [];

        // Creates an option object for a given price tier

        // Helper to retrieve price tiers for an add-on
        const getAddonTiers = (addOnId: string) => {
          const addon = this.addOnsQuery?.getAddOns().find((candidateAddon) => candidateAddon.getId() === addOnId);

          return addon?.priceTiers ?? [];
        };

        // Determine options based on the add-on ID
        switch (addonId) {
          case ADD_ON_IDS.SMS: {
            const tiers = getAddonTiers(ADD_ON_IDS.SMS);

            if (!tiers.length) return [];

            // display first tier by default
            const [firstTier] = tiers;

            // display the tier added from the SMS dialog as an extra one if it was added
            const LAST_TIER_INDEX_WE_DISPLAY_IN_UI_BY_DEFAULT = 0;
            const tierAddedFromSmsDialog = tiers.find(
              (tier, index) =>
                index > LAST_TIER_INDEX_WE_DISPLAY_IN_UI_BY_DEFAULT &&
                `${tier.tierMax}` === selectedAddOns.find(({ id }) => id === ADD_ON_IDS.SMS)?.option
            );

            const options: AddOnItemOption[] = [];
            const pushOption = (tier?: typeof firstTier, isPopular = false) => {
              if (tier) {
                options.push(
                  this.createOption(currencySymbol, tier, ADD_ON_IDS.SMS, discountPercentage, selectedAddOns, isPopular)
                );
              }
            };

            pushOption(tierAddedFromSmsDialog ?? firstTier);

            return options;
          }
          case ADD_ON_IDS.IBP: {
            const tiers = getAddonTiers(ADD_ON_IDS.IBP);

            if (!tiers.length) return [];

            const [firstTier] = tiers;

            // Only the first tier is used for IBP add-on
            return [this.createOption(currencySymbol, firstTier, ADD_ON_IDS.IBP, discountPercentage, selectedAddOns)];
          }
          case FE_ADD_ON_ID.XERO: {
            const isAdded = selectedAddOns.some((addOn) => addOn.id === FE_ADD_ON_ID.XERO);

            const buttonLabel = isAdded
              ? this.t('application-layer.add-ons.button-label-added')
              : this.t(`application-layer.add-ons.ibp.button-label`);

            return [
              {
                id: FE_ADD_ON_ID.XERO,
                header: this.t('application-layer.add-ons.xero.heading'),
                subHeader: this.t('application-layer.add-ons.xero.subheading'),
                price: this.t('application-layer.add-ons.xero.price'),
                isIncluded: false,
                isAdded,
                buttonLabel,
                buttonDisabled: this.isSingleBillChildAccount,
                onButtonClick: () => this.handlers().handleOptionClick(FE_ADD_ON_ID.XERO)(FE_ADD_ON_ID.XERO),
              },
            ];
          }
          default:
            return [];
        }
      },
      getAllSmsOptionsForDialog: () => {
        const tiers = this.addOnsQuery
          ?.getAddOns()
          .find((candidateAddon) => candidateAddon.getId() === ADD_ON_IDS.SMS)?.priceTiers;

        if (!tiers) return [];

        const tiersWithoutUnlimitedTier = tiers.filter((tier) => tier.tierMax !== -1);

        return tiersWithoutUnlimitedTier.map((tier) => ({
          id: `${tier.tierMax}`,
          label: `${NumberFormat.formatWithThousandsSeparator(
            tier.tierMin
          )} - ${NumberFormat.formatWithThousandsSeparator(tier.tierMax)}`,
          price: tier.price.format(tier.currencyCode, 0),
        }));
      },
      getDefaultPrecheckedSmsOption: (selectedAddOns: SelectedAddOn[]) => {
        const selectedOption = selectedAddOns.find(({ id }) => id === ADD_ON_IDS.SMS)?.option;

        if (selectedOption) {
          return selectedOption;
        }

        // just a random index; I put 2 because it goes after the first two options, which we display in the main UI
        const DEFAULT_PRECHECKED_INDEX = 1;

        const tiers = this.addOnsQuery
          ?.getAddOns()
          .find((candidateAddon) => candidateAddon.getId() === ADD_ON_IDS.SMS)?.priceTiers;

        if (!tiers) return '';

        return `${tiers[DEFAULT_PRECHECKED_INDEX].tierMax}`;
      },
      getIsSmsBundle: () => this.isSmsBundle,
      getSmsBundleTierOptions: () => {
        const tiers = this.addOnsQuery
          ?.getAddOns()
          .find((candidateAddon) => candidateAddon.getId() === ADD_ON_IDS.SMS)?.priceTiers;

        if (!tiers) {
          return [] as { value: string; label: string }[];
        }

        const tiersWithoutUnlimitedTier = tiers.filter((tier) => tier.tierMax !== -1);

        return tiersWithoutUnlimitedTier.map((tier) => {
          const min = NumberFormat.formatWithThousandsSeparator(tier.tierMin);
          const max = NumberFormat.formatWithThousandsSeparator(tier.tierMax);

          return {
            value: `${tier.tierMax}`,
            label: this.isValueFocusedMWebPlanSelectionEnabled
              ? this.t('application-layer.pricing-configurator.sms-tier-option-range' satisfies TranslationPaths, {
                  min,
                  max,
                })
              : `${min} - ${max}`,
          };
        });
      },
      getSmsBundleTierLabel: () =>
        this.isValueFocusedMWebPlanSelectionEnabled
          ? ''
          : this.t('application-layer.pricing-configurator.sms-messages-per-month' satisfies TranslationPaths),
      getSmsBundleTierSelectAriaLabel: (): string | undefined =>
        this.isValueFocusedMWebPlanSelectionEnabled
          ? this.t('application-layer.pricing-configurator.sms-messages-per-month' satisfies TranslationPaths)
          : undefined,
      getSmsBundleTierSelectPlaceholder: (): string =>
        this.t('application-layer.pricing-configurator.sms-bundle-tier-placeholder' satisfies TranslationPaths),
      getSmsBundleTierModalCopies: () => ({
        title: this.t('application-layer.pricing-configurator.sms-bundle-modal.title' satisfies TranslationPaths),
        closeAriaLabel: this.t(
          'application-layer.pricing-configurator.sms-bundle-modal.close-aria-label' satisfies TranslationPaths
        ),
      }),
      getSmsBundleTierModalRows: (prepay: string) => {
        const currencySymbol = this.billingAccountQuery?.getCurrencySymbol();
        const tiers = this.addOnsQuery
          ?.getAddOns()
          .find((candidateAddon) => candidateAddon.getId() === ADD_ON_IDS.SMS)?.priceTiers;
        const discountPercentage = this.paymentPlanQuery?.getDiscountPercentage(prepay);

        if (!tiers || !currencySymbol || discountPercentage === undefined) {
          return [];
        }

        const periodAbbrev = this.t('application-layer.add-ons.per-month-abbrev-mobile');

        return tiers
          .filter((tier) => tier.tierMax !== -1)
          .map((tier) => {
            const min = NumberFormat.formatWithThousandsSeparator(tier.tierMin);
            const max = NumberFormat.formatWithThousandsSeparator(tier.tierMax);
            const priceProps = tier.price.getPriceProps(discountPercentage);
            const decimalSuffix = priceProps.valueDecimal ? `.${priceProps.valueDecimal}` : '';

            return {
              value: `${tier.tierMax}`,
              label: `${min}-${max}`,
              priceLabel: `${currencySymbol}${priceProps.value}${decimalSuffix}${periodAbbrev}`,
              oldPriceLabel: priceProps.oldValue ? `${currencySymbol}${priceProps.oldValue}` : undefined,
            };
          });
      },
      getIsValueFocusedMWebTierSelectPresentation: () => this.isValueFocusedMWebPlanSelectionEnabled,
      getIsSingleBillChildAccount: () => this.isSingleBillChildAccount,
    };
  }

  public handlers() {
    return {
      handleOptionClick: (addOnId: string) => (option: string) => {
        if (this.isSingleBillChildAccount) {
          return;
        }

        const { selectedAddOns } = this.pageStore.getState();

        const optionWasAlreadySelected = selectedAddOns.some(
          (addOn) => addOn.id === addOnId && addOn.option === option
        );

        if (optionWasAlreadySelected) {
          this.removeAddOn(addOnId, 'user');
        } else {
          this.addAddOn(addOnId, option, 'user');
        }
      },
      handleSmsOptionChange: (option: string) => {
        this.pageStore.setState({ preSelectedSmsOption: option });
      },
      handleAddSmsOption: () => {
        if (this.isSingleBillChildAccount) {
          return;
        }

        const { preSelectedSmsOption, selectedAddOns } = this.pageStore.getState();

        const defaultPreselectedSmsOption = this.data().getDefaultPrecheckedSmsOption(selectedAddOns);

        const option = preSelectedSmsOption || defaultPreselectedSmsOption;

        if (option) {
          this.addAddOn(ADD_ON_IDS.SMS, option, 'user');
          this.pageStore.setState({ preSelectedSmsOption: null, smsOptionsDialogOpen: false });
        }
      },
      handleSmsOptionsDialogOpen: (open: boolean) => {
        this.pageStore.setState({ smsOptionsDialogOpen: open, preSelectedSmsOption: null });
      },
      onSmsBundleTierChange: (option: string) => {
        if (this.isSingleBillChildAccount) {
          return;
        }

        this.addAddOn(ADD_ON_IDS.SMS, option, 'user');
      },
      onSmsBundleTierModalSelect: (option: string) => {
        if (this.isSingleBillChildAccount) {
          return;
        }

        this.addAddOn(ADD_ON_IDS.SMS, option, 'user');
        this.pageStore.setState({ smsBundleTierModalOpen: false });
      },
      onSmsBundleTierModalOpenChange: (open: boolean) => {
        this.pageStore.setState({ smsBundleTierModalOpen: open });
      },
    };
  }

  public onMount = () => {
    const { addons: addonsFromUrl, deleteAddons } = this.urlAdapter.getAddonsParams();

    if (this.isSingleBillChildAccount) {
      if (addonsFromUrl) {
        deleteAddons();
      }

      this.pageStore.setState({ selectedAddOns: [] });

      return;
    }

    if (addonsFromUrl) {
      try {
        const addonsArray = JSON.parse(addonsFromUrl);
        const addonsFromState = this.pageStore.getState().selectedAddOns;

        // using setTimeout to ensure to avaid potential race conditions when updating the currency
        setTimeout(() => {
          // remove any addons which could have been loaded from local storage, but are not present in the URL
          addonsFromState.forEach(({ id }) => {
            if (addonsArray.some((addOn) => addOn.id === id)) {
              return;
            }

            this.removeAddOn(id, 'onMount');
          });

          addonsArray.forEach(({ id, option }) => {
            this.addAddOn(id, option, 'onMount');
          });
          deleteAddons();
          this.ensureSmsBundleSmsAddon();
        }, 100);

        return;
      } catch (error) {
        observabilityAppService.notify(error, { message: 'Failed to parse addons from URL', addonsFromUrl });
      }
    }

    const { selectedAddOns } = this.pageStore.getState();

    selectedAddOns.forEach(({ id, option }) => {
      addOnsEventBus.emit('addOnAdded', id, option, { source: 'onMount' });
    });

    this.ensureSmsBundleSmsAddon();
  };

  private ensureSmsBundleSmsAddon = () => {
    if (!this.isSmsBundle || !this.addOnsQuery) {
      return;
    }

    // SMS bundle mode no longer pre-selects a tier on mount: arriving with
    // `?aff-sms-bundle=true` leaves the dropdown on "None" and lets the user opt in.
    // We only intervene to drop a *stale* persisted SMS add-on whose option no longer
    // matches any visible tier (e.g. the unlimited tier or a tier that was removed
    // server-side). Without this guard the dropdown would render an invalid value.
    const { selectedAddOns } = this.pageStore.getState();
    const currentSmsAddOn = selectedAddOns.find((a) => a.id === ADD_ON_IDS.SMS);

    if (!currentSmsAddOn) {
      return;
    }

    const validTierOptions = this.data().getSmsBundleTierOptions();

    const hasValidOption = validTierOptions.some((option) => option.value === currentSmsAddOn.option);

    if (!hasValidOption) {
      this.removeAddOn(ADD_ON_IDS.SMS, 'onMount');
    }
  };

  private removeAddOn = (id: string, source: TEventSource) => {
    addOnsEventBus.emit('addOnRemoved', id, { source });

    this.pageStore.setState((state) => ({
      selectedAddOns: state.selectedAddOns.filter((addOn) => addOn.id !== id),
      ...(id === ADD_ON_IDS.SMS ? { preSelectedSmsOption: null, smsBundleTierModalOpen: false } : {}),
    }));
  };

  private addAddOn = (id: string, option: string, source: TEventSource) => {
    this.pageStore.setState(({ selectedAddOns }) => {
      const newAddOn = { id, option };

      return {
        selectedAddOns: selectedAddOns.filter((addOn) => addOn.id !== id).concat(newAddOn),
      };
    });

    addOnsEventBus.emit('addOnAdded', id, option, { source });
  };

  private createOption = (
    currencySymbol: string,
    priceTier: {
      tierMin: number;
      tierMax: number;
      price: PriceVO;
    },
    addOnId: string,
    discountPercentage: number,
    selectedAddOns: SelectedAddOn[],
    isPopular = false
  ): AddOnItemOption => {
    const basePrice = priceTier.price.priceAmount;

    const selectedOptionId = `${priceTier.tierMax}`; // this will be the usage we pass to order summary
    const isAdded = selectedAddOns.some((addOn) => addOn.id === addOnId && addOn.option === selectedOptionId);
    const isSmsAddOn = this.data().isSmsAddOn(addOnId); // otherwise it's ibp

    const header = isSmsAddOn
      ? `${NumberFormat.formatWithThousandsSeparator(priceTier.tierMin)} - ${NumberFormat.formatWithThousandsSeparator(
          priceTier.tierMax
        )}`
      : this.t('application-layer.add-ons.ibp.ibp-previews', { amount: this.PREVIEWS_AMOUNT });

    const subHeader = isSmsAddOn
      ? this.t('application-layer.add-ons.sms.sms-per-month')
      : this.t('application-layer.add-ons.per-month-abbrev');

    const price = {
      currency: currencySymbol,
      period: this.t('application-layer.add-ons.per-month-abbrev'),
      periodMobile: this.t('application-layer.add-ons.per-month-abbrev-mobile'),
      ...priceTier.price.getPriceProps(discountPercentage),
    };

    const label = isPopular ? { value: this.t('application-layer.add-ons.most-popular') } : undefined;

    const isIncluded = basePrice === 0;

    const buttonLabel = isAdded
      ? this.t('application-layer.add-ons.button-label-added')
      : this.t(`application-layer.add-ons.ibp.button-label`);

    return {
      id: priceTier.tierMin,
      header,
      subHeader,
      price,
      label,
      isIncluded,
      includedLabel: this.t('application-layer.add-ons.included-with-premium'),
      isAdded,
      buttonLabel,
      buttonDisabled: this.isSingleBillChildAccount,
      onButtonClick: () => this.handlers().handleOptionClick(addOnId)(selectedOptionId),
    };
  };
}
